import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Download, Loader2, Puzzle } from "lucide-react";

import { useToast } from "@/shared/hooks/use-toast";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { SkillsService, type SkillScopeTarget } from "../../services/skillsService";
import type { SkillStorefrontEntry } from "../../types/skill";
import { SkillInstallerProgress } from "./SkillInstallerProgress";
import {
  FilesStep,
  IntegrationsStep,
  OverviewStep,
  ParametersStep,
  ReviewStep,
} from "./steps";
import type { SkillInstallerStep } from "./types";

interface SkillInstallerWizardProps {
  skill: SkillStorefrontEntry | null;
  /** Onde instalar. Sai da navegação, e por isso o wizard não pergunta (AC13). */
  target: SkillScopeTarget;
  canManage: boolean;
  onOpenChange: (open: boolean) => void;
  onChanged: () => void;
}

/**
 * Instalação de uma skill em sequência guiada.
 *
 * Segue o modelo do `AgentInstaller` porque o problema é o mesmo: a decisão tem
 * partes distintas — o que o procedimento faz, o que vem no pacote, e com que
 * credencial ele vai rodar — e empilhar tudo num diálogo único obriga a pessoa
 * a decidir sem ter lido.
 *
 * **Passo sem conteúdo não entra na sequência.** Wizard com telas vazias é pior
 * que diálogo: skill sem requisito não mostra integrações, pacote de um arquivo
 * só não mostra a árvore. O passo de parâmetros entra aqui com o AB#85822, e a
 * montagem dinâmica é o que deixa isso ser um acréscimo e não uma reescrita.
 *
 * **Não há passo de escopo** (AB#85634, AC13): o nível vem de onde a pessoa está
 * navegando, e a api o tira da própria rota. Perguntar aqui seria pedir que ela
 * repetisse o que acabou de fazer ao escolher onde navegar.
 */
export const SkillInstallerWizard = ({
  skill,
  target,
  canManage,
  onOpenChange,
  onChanged,
}: SkillInstallerWizardProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [step, setStep] = useState(0);
  /**
   * Requisito → integrações escolhidas. É uma LISTA porque um requisito por
   * categoria aceita mais de uma (AC15): a organização pode ter um board de
   * produto e outro de engenharia, e a skill vale para os dois.
   */
  const [choices, setChoices] = useState<Record<string, string[]>>({});
  const [valores, setValores] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Cada skill recomeça do zero: herdar a escolha feita para a anterior
  // instalaria uma coisa enquanto a tela mostra outra.
  useEffect(() => {
    setStep(0);
    setChoices({});
    setValores({});
  }, [skill?.name]);

  const {
    data: detail,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["skill_catalog_detail", target.scope, target.productId ?? "-", skill?.name],
    enabled: !!skill?.name,
    queryFn: () => SkillsService.catalogDetail(target, skill!.name),
  });

  const requirements = useMemo(() => skill?.required_integrations ?? [], [skill]);

  // Os parâmetros que a skill declara. Vêm da vitrine, junto do resto, porque a
  // tela precisa saber o que perguntar ANTES de instalar.
  //
  // Memorizado porque o `?? []` cria um array novo a cada render, e ele é
  // dependência do `useMemo` dos passos — sem isto, a trilha inteira era
  // recalculada a cada tecla digitada num parâmetro.
  const parametros = useMemo(() => skill?.parameters ?? [], [skill?.parameters]);

  const steps = useMemo<SkillInstallerStep[]>(() => {
    const built: SkillInstallerStep[] = [{ id: "overview", label: t("skills.steps.overview") }];

    // A árvore só faz sentido quando há algo além do próprio procedimento, que
    // o passo de apresentação já mostra.
    if ((detail?.resources.length ?? 0) > 0) {
      built.push({ id: "files", label: t("skills.steps.files") });
    }
    if (requirements.length > 0) {
      built.push({ id: "integrations", label: t("skills.steps.integrations") });
    }

    // Só quando a skill declara algum: a maioria não declara, e um passo vazio
    // seria uma parada sem decisão.
    if (parametros.length > 0) {
      built.push({ id: "parameters", label: t("skills.steps.parameters") });
    }

    built.push({ id: "review", label: t("skills.steps.review") });

    return built;
  }, [detail, requirements, parametros, t]);

  if (!skill) return null;

  const current = steps[Math.min(step, steps.length - 1)];
  const isLast = step >= steps.length - 1;
  const unresolved = requirements.filter(
    (requirement) => (choices[requirement.requirement] ?? []).length === 0,
  );

  // Parâmetro obrigatório sem resposta trava o avanço, como requisito sem
  // escolha: nos dois casos a skill entraria sem ter o que precisa para rodar.
  const parametrosFaltando = parametros.filter(
    (parametro) => parametro.required && !(valores[parametro.key] ?? parametro.default),
  );

  // Avançar do passo de integrações exige as escolhas feitas: deixar passar
  // adiaria a recusa para a confirmação, longe de onde se resolve.
  const canAdvance =
    current?.id === "integrations"
      ? unresolved.length === 0
      : current?.id === "parameters"
        ? parametrosFaltando.length === 0
        : true;

  const install = async () => {
    setSaving(true);
    try {
      // Uma entrada por integração escolhida: o mesmo requisito pode aparecer
      // várias vezes, com integrações diferentes (AC15).
      const escolhidas = requirements.flatMap((requirement) =>
        (choices[requirement.requirement] ?? []).map((integrationBaseId) => ({
          requirement: requirement.requirement,
          integration_base_id: integrationBaseId,
        })),
      );

      const installed = await SkillsService.install(target, skill.name, escolhidas, valores);

      toast({
        title: t("skills.installed.title"),
        // A mensagem nomeia o NÍVEL certo (AC24). Antes ela dizia "neste
        // workspace" mesmo quando a instalação era no espaço pessoal.
        description: t(`skills.installed.description.${target.scope}`, {
          skill: skill.title || skill.name,
          version: installed.version,
        }),
      });
      onChanged();
      onOpenChange(false);
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("skills.installFailed"),
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!skill} onOpenChange={onOpenChange}>
      {/* Mais largo desde que os passos chegaram a seis (AB#85634): com a
          largura antiga a trilha apertava os rótulos, e o passo de Conteúdo — que
          divide árvore de arquivos e editor — ficava com as duas colunas
          estreitas demais para ler qualquer coisa. */}
      <DialogContent className="flex h-[85vh] max-w-5xl flex-col gap-0 p-0">
        {/* O cabeçalho diz O QUE se está fazendo, e não só com quem.
            Antes trazia apenas o nome da skill: a janela podia ser qualquer
            coisa — um detalhe, uma edição —, e só o botão do último passo
            revelava que aquilo era uma instalação. */}
        <DialogHeader className="border-b px-6 py-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Puzzle className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t("skills.installer.eyebrow")}
              </p>
              <DialogTitle className="truncate">{skill.title || skill.name}</DialogTitle>
              <DialogDescription className="truncate font-mono text-xs">
                {skill.name}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : isError || !detail ? (
          <div className="flex flex-1 items-center justify-center px-6">
            <p className="text-sm text-destructive">{t("skills.detailFailed")}</p>
          </div>
        ) : (
          <>
            <SkillInstallerProgress steps={steps} currentStep={step} />

            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
              {/* A licença vem DENTRO da apresentação: primeiro o que a skill
                  faz, e logo abaixo sob que termos ela pode ser usada. Era um
                  passo próprio, e uma etapa inteira para duas linhas de texto
                  custava mais atenção do que entregava. */}
              {current?.id === "overview" && (
                <OverviewStep
                  detail={detail}
                  license={{
                    cacheKey: [target.scope, target.productId ?? "-"],
                    readFile: (path) => SkillsService.catalogResource(target, detail.name, path),
                  }}
                />
              )}
              {current?.id === "files" && (
                <FilesStep
                  detail={detail}
                  cacheKey={[target.scope, target.productId ?? "-"]}
                  readFile={(path) => SkillsService.catalogResource(target, detail.name, path)}
                />
              )}
              {current?.id === "parameters" && (
                <ParametersStep
                  parameters={parametros}
                  values={valores}
                  onChange={(key, value) =>
                    setValores((anteriores) => ({ ...anteriores, [key]: value }))
                  }
                />
              )}
              {current?.id === "integrations" && (
                <IntegrationsStep
                  skill={skill}
                  choices={choices}
                  onToggle={(requirement, integrationBaseId) =>
                    setChoices((previous) => {
                      const atuais = previous[requirement] ?? [];
                      const proximas = atuais.includes(integrationBaseId)
                        ? atuais.filter((id) => id !== integrationBaseId)
                        : [...atuais, integrationBaseId];

                      // Requisito sem nenhuma escolha sai do mapa em vez de ficar
                      // com lista vazia: `unresolved` conta chave ausente, e uma
                      // lista vazia passaria por escolha feita.
                      const next = { ...previous };
                      if (proximas.length > 0) next[requirement] = proximas;
                      else delete next[requirement];
                      return next;
                    })
                  }
                />
              )}
              {current?.id === "review" && (
                <ReviewStep skill={skill} detail={detail} choices={choices} scope={target.scope} />
              )}
            </div>

            <div className="flex items-center justify-between border-t px-6 py-4">
              <Button
                variant="ghost"
                disabled={step === 0}
                onClick={() => setStep((value) => value - 1)}
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                {t("skills.steps.back")}
              </Button>

              {isLast ? (
                canManage &&
                !skill.installed && (
                  <Button onClick={install} disabled={saving || unresolved.length > 0}>
                    {saving ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="mr-2 h-4 w-4" />
                    )}
                    {/* "Instalar", sem citar o nível (AC14): a navegação já o diz. */}
                    {t("skills.install")}
                  </Button>
                )
              ) : (
                <Button disabled={!canAdvance} onClick={() => setStep((value) => value + 1)}>
                  {canAdvance
                    ? t("skills.steps.next")
                    : t("skills.integrations.pending", { count: unresolved.length })}
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
